import { SearchIcon } from '@heroicons/react/solid';
import Button from 'components/atoms/Button';
import TabLink from 'components/atoms/TabLink';
import ClientFeedbackSearchBar from 'components/molecules/ClientFeedbackSearchBar';
import LeadFeedbackSearchBar from 'components/molecules/LeadFeedbackSearchBar';
import Search from 'components/molecules/Search';
import SlideOver from 'components/molecules/SlideOver';
import TrafficControllerFeedbackSearchBar from 'components/molecules/TrafficControllerFeedbackSearchBar';
import CommentPanel from 'components/organisms/CommentPanel';
import ReportPanel from 'components/organisms/ReportPanel';
import SurveyResultsPanel from 'components/organisms/SurveyResultsPanel';
import Table from 'components/organisms/Table';
import MainLayout from 'components/templates/MainLayout';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	useCreateSurveySubmissionActivityMutation,
	useDeleteSurveySubmissionActivityMutation,
	useGetSurveySubmissionActivityMutation,
	useGetVehicleSurveySubmissionsMutation,
	useUpdateSurveySubmissionActivityMutation,
} from 'redux/api/surveys';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
	closeCommentModal,
	closeSurveyModal,
	closeSurveyReportModal,
} from 'redux/slices/surveys';
import { IQueryPeriod } from 'types/VehicleFaults';
import SurveySearchBar from 'components/molecules/SurveySearchBar';
import { hasAccess } from 'utils/permissions';

const Surveys = () => {
	const [period, setPeriod] = useState<IQueryPeriod>('today');
	const surveyState = useAppSelector((state) => state.surveys);
	const [queryString, setQueryString] = useState<string>('');
	const [surveyType, setSurveyType] = useState(surveyState.surveyType);
	const [getSurveySubmissions, { data }] =
		useGetVehicleSurveySubmissionsMutation();
	const dispatch = useAppDispatch();
	const userPermission = useAppSelector((state) => state.user.permissions);
	const [createSurveySubmissionActivity] =
		useCreateSurveySubmissionActivityMutation();
	const [deleteSurveySubmissionActivity] =
		useDeleteSurveySubmissionActivityMutation();
	const [updateSurveySubmissionActivity] =
		useUpdateSurveySubmissionActivityMutation();
	const [getSurveySubmissionActivity, { data: activityData }] =
		useGetSurveySubmissionActivityMutation();
	const { uid } = useParams();
	const [clientOpen, setClientOpen] = useState(false);
	const [teamLeadsOpen, setTeamLeadsOpen] = useState(false);
	const [trafficControllerOpen, setTrafficControllerOpen] = useState(false);
	const [searchOpen, setSearchOpen] = useState(false);

	useEffect(() => {
		if (uid) {
			setQueryString(`uid=${uid}&`);
		}
	}, [uid]);

	useEffect(() => {
		setSurveyType(surveyState.surveyType);
	}, [surveyState.surveyType]);

	useEffect(() => {
		getSurveySubmissions(`${queryString}period=${period}&type=${surveyType}`);
	}, [queryString, getSurveySubmissions, surveyType, period]);

	const handleCloseComments = () => {
		dispatch(closeCommentModal());
	};

	const hasManagerAccess = () => {
		return hasAccess(
			userPermission,
			'manager',
			'survey_level',
			'write',
			surveyState.surveyBranchId
		);
	};

	const headings = [
		'Recipient',
		'Survey Name',
		'Date Sent',
		'Branch',
		'Status',
	];

	const handleCloseSurveyPanel = () => {
		dispatch(closeSurveyModal());
	};

	const handleCloseSurveyReportPanel = () => {
		dispatch(closeSurveyReportModal());
	};

	const handleOpenFeedbackReport = () => {
		if (surveyType === 'Clients') {
			setClientOpen(true);
		}

		if (surveyType === 'Team Leads') {
			setTeamLeadsOpen(true);
		}

		if (surveyType === 'Traffic Controllers') {
			setTrafficControllerOpen(true);
		}
	};

	const handleOpenSearch = () => {
		setSearchOpen(true);
	};

	return (
		<MainLayout url="surveys" surveyDropdown={true} setFilters={setQueryString}>
			<main className="">
				<div className="flex items-center justify-center py-7 px-4 sticky top-0 z-30 bg-white">
					<Button type="secondary" onClick={handleOpenSearch}>
						<SearchIcon className="h-5 w-5 mr-1" />
						Search
					</Button>
					<div className="flex items-center justify-center flex-1 w-full space-x-4">
						<TabLink
							onClick={() => {
								// reset the query string when we toggle the period to clear search filters
								setQueryString('');
								setPeriod('today');
							}}
							active={period === 'today'}
						>
							Today
						</TabLink>
						<TabLink
							onClick={() => {
								setQueryString('');
								setPeriod('week');
							}}
							active={period === 'week'}
						>
							This Week
						</TabLink>
						<TabLink
							onClick={() => {
								setQueryString('');
								setPeriod('month');
							}}
							active={period === 'month'}
						>
							This Month
						</TabLink>
						<TabLink
							onClick={() => {
								setQueryString('');
								setPeriod('all');
							}}
							active={period === 'all'}
						>
							All
						</TabLink>
					</div>
					<Button
						className="font-bold"
						onClick={handleOpenFeedbackReport}
						isDisabled={surveyType === 'All'}
					>
						Feedback Report
					</Button>
				</div>
				<Table
					headings={headings}
					data={data?.data}
					refetchData={() => getSurveySubmissions(queryString)}
					removeIdFromList={(id: string | number) => {}}
				/>
			</main>
			<SlideOver open={surveyState.commentsOpen} onClose={handleCloseComments}>
				<CommentPanel
					id={surveyState.commentSurveyId}
					onClose={handleCloseComments}
					createActivity={(payload) => createSurveySubmissionActivity(payload)}
					updateActivity={updateSurveySubmissionActivity}
					deleteActivity={deleteSurveySubmissionActivity}
					activityData={activityData}
					getActivity={getSurveySubmissionActivity}
					activityType="survey_submission"
					hasPermissionAccess={hasManagerAccess}
				/>
			</SlideOver>
			<SlideOver
				open={surveyState.surveyPanelOpen}
				onClose={handleCloseSurveyPanel}
			>
				<SurveyResultsPanel />
			</SlideOver>
			<SlideOver
				open={surveyState.surveyReportPanelOpen}
				onClose={handleCloseSurveyReportPanel}
			>
				<ReportPanel type={surveyState.surveyType} />
			</SlideOver>
			{/* Search Panels */}
			<Search open={clientOpen} setOpen={setClientOpen} searchHeading="Clients">
				<ClientFeedbackSearchBar
					setFilters={setQueryString}
					setOpen={setClientOpen}
				/>
			</Search>
			<Search
				open={teamLeadsOpen}
				setOpen={setTeamLeadsOpen}
				searchHeading="Team Lead"
			>
				<LeadFeedbackSearchBar
					setOpen={setTeamLeadsOpen}
					setFilters={setQueryString}
				/>
			</Search>
			<Search
				open={trafficControllerOpen}
				setOpen={setTrafficControllerOpen}
				searchHeading="Traffic Controller"
			>
				<TrafficControllerFeedbackSearchBar
					setOpen={setTrafficControllerOpen}
					setFilters={setQueryString}
				/>
			</Search>
			<Search open={searchOpen} setOpen={setSearchOpen} searchType={'survey'}>
				<SurveySearchBar setFilters={setQueryString} setOpen={setSearchOpen} />
			</Search>
		</MainLayout>
	);
};

export default Surveys;
